/**
 * 모니터링(사업장 현황)의 작은 부품 — 그림판(`SiteBoard.tsx`)과 지도 위 카드·공장 판(`screens/Sites.tsx`)이 함께 쓴다.
 * 화면 노드 한 장, 온라인/전체 숫자, 가동률 막대, 출처 이름.
 */
import type { ReactNode } from 'react';
import { Monitor, MonitorX } from 'lucide-react';
import { StatusDot } from './ui.js';
import { DisplayThumbnail } from './DisplayThumbnail.js';
import { healthTone, LOCAL_SITE_NAME, type Counts, type SiteDisplay, type SiteSource } from '../sites.js';

const TONE_BAR: Record<ReturnType<typeof healthTone>, string> = {
  ok: 'bg-emerald-500',
  warn: 'bg-amber-500',
  danger: 'bg-rose-500',
  muted: 'bg-slate-300',
};

export const sourceLabel = (source: SiteSource): string => (source.kind === 'local' ? LOCAL_SITE_NAME : source.name);

/**
 * 화면 노드 — 썸네일 한 장과 이름. 원격 서버의 화면은 그림 대신 상태를 그린다. `data-focus` 는 그림판이 그 노드로
 * 날아가 맞출 때 찾는 표지다.
 */
export function DisplayNode({
  display,
  selected,
  onPick,
}: {
  display: SiteDisplay;
  selected: boolean;
  onPick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      className={`site-node ${display.online ? '' : 'is-off'} ${display.error ? 'is-error' : ''} ${selected ? 'is-selected' : ''}`}
      onClick={onPick}
      title={display.error ?? undefined}
      data-focus={`d:${display.key}`}
    >
      <span className="site-node-shot">
        <NodeShot display={display} />
      </span>
      <span className="site-node-name">
        <StatusDot online={display.online} />
        <span className="min-w-0 flex-1 truncate">{display.name || '(이름 없음)'}</span>
      </span>
      <span className="site-node-sub text-rose-700">{display.error ?? ''}</span>
    </button>
  );
}

export function NodeShot({ display, large = false }: { display: SiteDisplay; large?: boolean }): ReactNode {
  if (display.card) return <DisplayThumbnail card={display.card} asOf={display.source.asOf} />;
  return (
    <span
      className={`flex h-full w-full flex-col items-center justify-center gap-1 ${display.online ? 'bg-slate-800 text-slate-300' : 'bg-red-950/95 text-red-200'}`}
    >
      {display.online ? (
        <Monitor className={large ? 'h-8 w-8' : 'h-5 w-5'} strokeWidth={1.6} />
      ) : (
        <MonitorX className={large ? 'h-8 w-8' : 'h-5 w-5'} strokeWidth={1.6} />
      )}
      <span className={large ? 'text-[13px] font-semibold' : 'text-[11px] font-semibold'}>
        {display.online ? '온라인' : '오프라인'}
      </span>
      <span className="text-[10.5px] opacity-80">원격 서버 · 썸네일 없음</span>
    </span>
  );
}

export function CountLine({ counts, stale }: { counts: Counts; stale: boolean }): ReactNode {
  return (
    <span className={`whitespace-nowrap text-[12px] ${stale ? 'text-slate-400' : 'text-slate-500'}`}>
      온라인 {counts.online} / {counts.total}
      {counts.offline > 0 ? (
        <span className={stale ? '' : ' font-semibold text-rose-700'}> · 오프라인 {counts.offline}</span>
      ) : null}
    </span>
  );
}

/**
 * 온라인·오프라인 막대 — 초록이 켜진 몫, 그 뒤가 꺼진 몫(일부면 주황, 절반 넘으면 빨강). 다 꺼져도 빨간 막대가 차서
 * 빈 막대(= 화면 없음)와 헷갈리지 않는다. 끊긴 출처는 통째로 회색.
 */
export function HealthBar({
  counts,
  stale,
  className = '',
}: {
  counts: Counts;
  stale: boolean;
  className?: string;
}): ReactNode {
  const tone = healthTone(counts, stale);
  const pct = (value: number): string => `${counts.total > 0 ? Math.round((value / counts.total) * 100) : 0}%`;
  return (
    <span
      className={`flex h-1.5 shrink-0 overflow-hidden rounded-full bg-slate-100 ${className}`}
      role="img"
      aria-label={`온라인 ${counts.online} / ${counts.total}${counts.offline > 0 ? ` · 오프라인 ${counts.offline}` : ''}`}
    >
      {stale ? (
        <span className={`block h-full w-full ${TONE_BAR.muted}`} />
      ) : (
        <>
          <span className={`block h-full ${TONE_BAR.ok}`} style={{ width: pct(counts.online) }} />
          <span
            className={`block h-full ${TONE_BAR[tone === 'danger' ? 'danger' : 'warn']}`}
            style={{ width: pct(counts.offline) }}
          />
        </>
      )}
    </span>
  );
}
